import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { ArrowUpRight, HelpCircle } from 'lucide-react';
import BlurText from './BlurText';
import { useCountryLink } from '../context/CountryContext';
import { faqs } from '../mock';

const SERIF = { fontFamily: "'Nacelle', sans-serif", fontWeight: 600 };
const SANS = { fontFamily: "'Nacelle', sans-serif", fontWeight: 400 };

const fadeUp = (delay = 0) => ({
  initial: { filter: 'blur(10px)', opacity: 0, y: 20 },
  whileInView: { filter: 'blur(0px)', opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.8, ease: 'easeOut', delay },
});

const FAQSection = () => {
  const { link } = useCountryLink();
  const headRef = useRef(null);
  const headInView = useInView(headRef, { once: true, amount: 0.3 });

  return (
    <section
      id="faq"
      className="relative bg-black py-20 sm:py-24 lg:py-32 overflow-hidden"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(255,110,0,0.08) 0%, rgba(255,110,0,0) 50%)',
        }}
      />

      <div className="relative max-w-3xl mx-auto px-5 lg:px-8">
        {/* Heading */}
        <div className="text-center" ref={headRef}>
          <motion.div
            {...fadeUp(0.1)}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-white/70 text-[11px] tracking-[0.2em] uppercase"
            style={SANS}
          >
            <HelpCircle className="w-3.5 h-3.5" />
            Frequently Asked
          </motion.div>

          <div className="mt-6" style={SERIF}>
            {headInView && (
              <BlurText
                text="Honest Answers. No Fluff."
                className="text-4xl sm:text-5xl md:text-6xl text-white leading-[0.95] tracking-[-1.5px] sm:tracking-[-2px]"
              />
            )}
          </div>

          <motion.p
            {...fadeUp(0.5)}
            className="mt-6 text-white/60 text-[14.5px] sm:text-[15.5px] max-w-xl mx-auto leading-relaxed"
            style={SANS}
          >
            Everything you need to know before joining. If you have more
            questions, the live webinar covers the rest.
          </motion.p>
        </div>

        {/* FAQs */}
        <motion.div {...fadeUp(0.2)} className="mt-12 sm:mt-14">
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-white/[0.025] border border-white/10 rounded-2xl px-5 sm:px-6 data-[state=open]:border-[#FF8533]/30 data-[state=open]:bg-white/[0.045] transition-all"
              >
                <AccordionTrigger
                  className="text-white text-[15px] sm:text-[16.5px] text-left hover:no-underline py-5 tracking-[-0.3px]"
                  style={SERIF}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent
                  className="text-white/65 text-[13.5px] sm:text-[14px] leading-relaxed pb-5"
                  style={SANS}
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Closing CTA */}
        <motion.div
          {...fadeUp(0.1)}
          className="mt-14 text-center"
        >
          <p
            className="text-white/55 text-[13.5px] sm:text-[14.5px]"
            style={SANS}
          >
            Still on the fence? Grab it while access is open.
          </p>
          <a
            href={link} target="_blank" rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 bg-[#FF6E00] hover:bg-[#FF8533] text-white text-[14.5px] sm:text-[15px] px-6 py-3.5 rounded-full transition-colors shadow-[0_18px_40px_-15px_rgba(255,110,0,0.7)]"
            style={{ ...SANS, fontWeight: 500 }}
          >
            Get MBE Alert Now <ArrowUpRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
